import { FunctionComponent } from 'preact';

import { lazy, ComponentType } from 'preact/compat';

interface LazyWithRetryOptions {
	maxRetries?: number;
	retryDelay?: number;
	onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAY = 1000;

/**
 * Type for a module with a default component export.
 * Accepts modules that have a default export which is a component.
 */
type ComponentModule = {
	default: FunctionComponent | ComponentType<object>;
	[key: string]: unknown;
};

/**
 * Creates a lazy-loaded component with automatic retry logic for failed imports.
 * Useful for handling network failures or chunk loading errors.
 *
 * @param importFn - Dynamic import function that returns a module with a default export
 * @param options - Configuration options for retry behavior
 * @returns A lazy-loaded Preact FunctionComponent
 *
 * @example
 * ```tsx
 * const LazyComponent = lazyWithRetry(
 *   () => import('./MyComponent.tsx'),
 *   { maxRetries: 3, retryDelay: 1000 }
 * );
 *
 * // Use with Suspense
 * <Suspense fallback={<Loading />}>
 *   <LazyComponent />
 * </Suspense>
 * ```
 */
export function lazyWithRetry(
	importFn: () => Promise<ComponentModule>,
	options: LazyWithRetryOptions = {},
): FunctionComponent {
	const {
		maxRetries = DEFAULT_MAX_RETRIES,
		retryDelay = DEFAULT_RETRY_DELAY,
		onRetry,
	} = options;

	const retryImport = async (retriesLeft: number): Promise<ComponentModule> => {
		try {
			return await importFn();
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));

			if (retriesLeft <= 0) {
				throw err;
			}

			const currentAttempt = maxRetries - retriesLeft + 1;
			onRetry?.(currentAttempt, err);

			// Wait before retrying
			await new Promise((resolve) => setTimeout(resolve, retryDelay));

			return retryImport(retriesLeft - 1);
		}
	};

	return lazy(() => retryImport(maxRetries)) as unknown as FunctionComponent;
}

/**
 * Creates a lazy-loaded component with retry logic and a custom fallback for errors.
 * This version allows you to provide a fallback component to render if all retries fail.
 *
 * @param importFn - Dynamic import function
 * @param FallbackComponent - Component to render if loading fails after all retries
 * @param options - Configuration options for retry behavior
 * @returns A lazy-loaded Preact FunctionComponent
 *
 * @example
 * ```tsx
 * const ErrorFallback = ({ error }) => <div>Failed to load: {error?.message}</div>;
 *
 * const LazyComponent = lazyWithRetryAndFallback(
 *   () => import('./MyComponent.tsx'),
 *   ErrorFallback,
 *   { maxRetries: 3 }
 * );
 * ```
 */
export function lazyWithRetryAndFallback(
	importFn: () => Promise<ComponentModule>,
	FallbackComponent: FunctionComponent<{ error?: Error }>,
	options: LazyWithRetryOptions = {},
): FunctionComponent {
	const {
		maxRetries = DEFAULT_MAX_RETRIES,
		retryDelay = DEFAULT_RETRY_DELAY,
		onRetry,
	} = options;

	const retryImport = async (retriesLeft: number): Promise<ComponentModule> => {
		try {
			return await importFn();
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));

			if (retriesLeft <= 0) {
				// Return fallback component instead of throwing
				return { default: FallbackComponent };
			}

			const currentAttempt = maxRetries - retriesLeft + 1;
			onRetry?.(currentAttempt, err);

			await new Promise((resolve) => setTimeout(resolve, retryDelay));

			return retryImport(retriesLeft - 1);
		}
	};

	return lazy(() => retryImport(maxRetries)) as unknown as FunctionComponent;
}

export default lazyWithRetry;
